import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Lock, CheckCircle, Mail, ShoppingBag, X } from 'lucide-react';
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/vE7H6xvpbpGBjPcBJ1gWz7/WhatsApp_Image_2026-03-13_at_7.13.29_PM.jpg';
// Reusable animation wrapper for sections
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6,
  delay
}} data-id="element-0">
    {children}
  </motion.div>;
// FAQ Item Component
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gold/30 last:border-0" data-id="element-1">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-4 flex justify-between items-center text-left focus:outline-none group" data-id="element-2">
        <span className="text-lg md:text-xl font-bold text-white group-hover:text-gold transition-colors pr-4" data-id="element-3">
          {question}
        </span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.3
      }} className="flex-shrink-0 text-gold" data-id="element-4">
          <ChevronDown size={24} data-id="element-5" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-6">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="overflow-hidden" data-id="element-7">
            <p className="pb-4 text-body-gray leading-relaxed" data-id="element-8">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
// Reviews data
const reviews = [{
  name: 'Carlos M.',
  location: 'Bogotá, Colombia',
  stars: 5,
  text: 'Llevaba 4 años apostando. En 3 semanas con este sistema dejé de sentir el impulso. Increíble.'
}, {
  name: 'Andrés R.',
  location: 'Medellín, Colombia',
  stars: 5,
  text: 'El Tomo 2 me abrió los ojos. Entendí cómo me manipulaban y pude cortar el ciclo.'
}, {
  name: 'Luis F.',
  location: 'Ciudad de México',
  stars: 5,
  text: 'Recuperé mi relación y mis finanzas. Este material vale 10 veces lo que cuesta.'
}, {
  name: 'Miguel A.',
  location: 'Buenos Aires, Argentina',
  stars: 5,
  text: 'Pensé que era débil. El libro me enseñó que era una trampa diseñada. Ahora soy libre.'
}];
const BOOK_PAGES = [{
  image: 'https://cdn.magicpatterns.com/uploads/nYAqtg6jrLPcLBrEoNjm6i/WhatsApp_Image_2026-03-13_at_7.43.21_PM_(1).jpg',
  label: 'TOMO 1',
  caption: 'La Diferencia Crítica: Jugador Social vs. Jugador Patológico'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/kY4dvrjYQhAtfZrYA6YL98/WhatsApp_Image_2026-03-13_at_7.43.21_PM.jpg',
  label: 'TOMO 1',
  caption: 'Señales de Alerta: De los 20 a los 65 Años'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/mFcZiQAMz64CU7yeg3xDGZ/WhatsApp_Image_2026-03-13_at_7.43.22_PM_(1).jpg',
  label: 'TOMO 2',
  caption: 'El Cuerpo Reacciona: Lo que la Ludopatía Hace a Tu Salud'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/kiRt16N9XgrmrWycuqmtTn/WhatsApp_Image_2026-03-13_at_7.43.22_PM.jpg',
  label: 'TOMO 3',
  caption: 'Quiniela, Lotería y Carreras: La Trampa de la Tradición'
}];
const BookCarousel = () => {
  const [current, setCurrent] = useState(0);
  const prev = () => setCurrent(c => (c - 1 + BOOK_PAGES.length) % BOOK_PAGES.length);
  const next = () => setCurrent(c => (c + 1) % BOOK_PAGES.length);
  return <div className="mt-2" data-id="element-9">
      <h3 className="text-2xl md:text-3xl text-gold font-black uppercase text-center mb-4 text-glow-gold" data-id="element-10">
        📖 Mira lo que hay dentro
      </h3>
      <div className="relative max-w-sm mx-auto" data-id="element-11">
        {/* Card */}
        <div className="bg-darker-surface rounded-2xl border border-gold/20 overflow-hidden shadow-2xl" data-id="element-12">
          <AnimatePresence mode="wait" data-id="element-13">
            <motion.div key={current} initial={{
            opacity: 0,
            x: 40
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -40
          }} transition={{
            duration: 0.35
          }} data-id="element-14">
              <img src={BOOK_PAGES[current].image} alt={BOOK_PAGES[current].caption} className="w-full object-cover" style={{
              maxHeight: '520px',
              objectPosition: 'top'
            }} data-id="element-15" />
            </motion.div>
          </AnimatePresence>
          <div className="px-4 py-3 flex items-start gap-3" data-id="element-16">
            <span className="bg-gold/20 text-gold font-bold px-3 py-1 rounded-full text-xs tracking-wider flex-shrink-0" data-id="element-17">
              {BOOK_PAGES[current].label}
            </span>
            <p className="text-body-gray text-sm leading-snug" data-id="element-18">
              {BOOK_PAGES[current].caption}
            </p>
          </div>
        </div>

        {/* Prev / Next buttons */}
        <button onClick={prev} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-5 w-10 h-10 rounded-full bg-carbon border border-gold/30 text-gold flex items-center justify-center hover:bg-gold/10 transition-colors shadow-lg" aria-label="Anterior" data-id="element-19">
          ‹
        </button>
        <button onClick={next} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-5 w-10 h-10 rounded-full bg-carbon border border-gold/30 text-gold flex items-center justify-center hover:bg-gold/10 transition-colors shadow-lg" aria-label="Siguiente" data-id="element-20">
          ›
        </button>
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-2 mt-3" data-id="element-21">
        {BOOK_PAGES.map((_, i) => <button key={i} onClick={() => setCurrent(i)} className={`w-2.5 h-2.5 rounded-full transition-all ${i === current ? 'bg-gold scale-125' : 'bg-gold/30'}`} aria-label={`Página ${i + 1}`} data-id="element-22" />)}
      </div>
    </div>;
};
// Purchase notification data
const RECENT_PURCHASES = [{
  name: 'Martín G.',
  province: 'Buenos Aires',
  time: 'hace 2 min'
}, {
  name: 'Lucía F.',
  province: 'Córdoba',
  time: 'hace 4 min'
}, {
  name: 'Diego R.',
  province: 'Santa Fe',
  time: 'hace 6 min'
}, {
  name: 'Sofía M.',
  province: 'Mendoza',
  time: 'hace 8 min'
}, {
  name: 'Pablo L.',
  province: 'Tucumán',
  time: 'hace 10 min'
}, {
  name: 'Camila A.',
  province: 'Salta',
  time: 'hace 12 min'
}, {
  name: 'Federico P.',
  province: 'Entre Ríos',
  time: 'hace 14 min'
}, {
  name: 'Valentina S.',
  province: 'Misiones',
  time: 'hace 16 min'
}, {
  name: 'Joaquín C.',
  province: 'Neuquén',
  time: 'hace 19 min'
}, {
  name: 'Agustina T.',
  province: 'Chaco',
  time: 'hace 22 min'
}, {
  name: 'Nicolás B.',
  province: 'Río Negro',
  time: 'hace 25 min'
}, {
  name: 'Florencia D.',
  province: 'San Juan',
  time: 'hace 28 min'
}];
const PurchasePopup = () => {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (dismissed) return;
    // Initial delay before first popup
    const initial = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(initial);
  }, [dismissed]);
  useEffect(() => {
    if (dismissed) return;
    // Cycle every 12 seconds: show for 7s, hide for 5s, then next
    const cycle = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex(i => (i + 1) % RECENT_PURCHASES.length);
        setVisible(true);
      }, 500);
    }, 12000);
    return () => clearInterval(cycle);
  }, [dismissed]);
  if (dismissed) return null;
  const purchase = RECENT_PURCHASES[index];
  return <AnimatePresence data-id="element-23">
      {visible && <motion.div key={index} initial={{
      opacity: 0,
      x: -40,
      y: 20
    }} animate={{
      opacity: 1,
      x: 0,
      y: 0
    }} exit={{
      opacity: 0,
      x: -40
    }} transition={{
      duration: 0.4,
      ease: 'easeOut'
    }} className="fixed bottom-4 left-4 z-50 max-w-[280px] bg-darker-surface border border-gold/30 rounded-xl shadow-2xl px-3 py-2.5 flex items-center gap-2.5" data-id="element-24">
          <div className="w-9 h-9 rounded-full bg-gold/15 flex items-center justify-center flex-shrink-0" data-id="element-25">
            <ShoppingBag className="text-gold" size={16} data-id="element-26" />
          </div>
          <div className="flex-grow min-w-0" data-id="element-27">
            <p className="text-white text-xs font-bold leading-tight truncate" data-id="element-28">
              {purchase.name}{' '}
              <span className="text-body-gray font-normal" data-id="element-29">
                de {purchase.province}
              </span>
            </p>
            <p className="text-gold text-[10px] mt-0.5 leading-tight" data-id="element-30">
              acaba de comprar ·{' '}
              <span className="text-white/40" data-id="element-31">{purchase.time}</span>
            </p>
          </div>
          <button onClick={() => setDismissed(true)} className="text-white/30 hover:text-white/70 transition-colors flex-shrink-0" aria-label="Cerrar" data-id="element-32">
            <X size={14} data-id="element-33" />
          </button>
        </motion.div>}
    </AnimatePresence>;
};
export function App() {
  const [timeLeft, setTimeLeft] = useState(15 * 60); // 15 minutes in seconds
  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);
  const minutes = String(Math.floor(timeLeft / 60)).padStart(2, '0');
  const seconds = String(timeLeft % 60).padStart(2, '0');
  return <div className="min-h-screen bg-carbon overflow-x-hidden" data-id="element-34">
      {/* SECTION 1: HERO */}
      <section className="relative pt-3 pb-6 md:pt-8 md:pb-12 px-4 max-w-7xl mx-auto" data-id="element-35">
        {/* Subtle countdown timer */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1
      }} className="flex items-center justify-center gap-2 mb-2" data-id="element-36">
          <span className="text-white/40 text-xs uppercase tracking-widest" data-id="element-37">
            Oferta expira en
          </span>
          <span className="text-gold font-bold text-sm tabular-nums tracking-wider" data-id="element-38">
            {minutes}:{seconds}
          </span>
          <span className="w-1.5 h-1.5 rounded-full bg-casino-red animate-pulse" data-id="element-39"></span>
        </motion.div>

        {/* Title above everything */}
        <motion.h1 initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7
      }} className="text-[16px] md:text-3xl lg:text-4xl font-black mb-3 md:mb-6 uppercase text-[#D69F1E] text-center leading-tight" data-id="element-40">
          Recupera El Dinero, La Confianza Y La Tranquilidad Que La Ludopatía Te
          Robó, Incluso Si Hoy Sientes Que Tocaste Fondo.
        </motion.h1>

        <div className="flex flex-col md:flex-row items-center gap-3 md:gap-8" data-id="element-41">
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8
        }} className="w-full md:w-1/2 relative flex flex-col items-center" data-id="element-42">
            <div className="absolute inset-0 bg-gold/10 blur-[100px] rounded-full pointer-events-none" data-id="element-43"></div>
            <img src={HERO_IMAGE} alt="El Casi Gano - 3 Tomos" className="relative z-10 w-full max-w-[180px] sm:max-w-[220px] md:max-w-[280px] mx-auto drop-shadow-2xl rounded-xl" data-id="element-44" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 50
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="w-full md:w-1/2 flex flex-col items-center md:items-start text-center md:text-left" data-id="element-45">
            <span className="text-casino-red font-bold uppercase tracking-widest text-xs md:text-base mb-2 md:mb-3 flex items-center gap-2" data-id="element-46">
              <span className="animate-pulse" data-id="element-47">🚨</span> ÚLTIMA OPORTUNIDAD PARA
              RECUPERAR TU VIDA
            </span>
            <p className="text-sm md:text-lg text-body-gray mb-3 md:mb-5 leading-snug md:leading-relaxed max-w-xl" data-id="element-48">
              Deja de mentir. Deja de perder. Aprende a desactivar la trampa
              psicológica de las apuestas y recupera tu dinero y tu paz.
            </p>
            <a href="#oferta" onClick={e => {
            e.preventDefault();
            document.getElementById('oferta')?.scrollIntoView({
              behavior: 'smooth'
            });
          }} className="w-full sm:w-auto bg-casino-red text-white font-black uppercase py-3 md:py-4 px-6 md:px-8 rounded-lg text-base md:text-xl shadow-glow-red hover:bg-red-600 transition-all animate-cta-pulse text-center inline-block" data-id="element-49">
              SÍ, QUIERO EL MATERIAL COMPLETO AHORA
            </a>
          </motion.div>
        </div>
      </section>

      {/* Subtle Separator */}
      <div className="h-px w-full bg-gradient-to-r from-transparent via-gold/30 to-transparent" data-id="element-50"></div>

      {/* SECTION 2: EL PROBLEMA */}
      <section className="py-8 md:py-12 bg-dark-surface px-4" data-id="element-51">
        <div className="max-w-6xl mx-auto" data-id="element-52">
          <FadeIn data-id="element-53">
            <h2 className="text-3xl md:text-5xl text-gold text-center mb-6 text-glow-gold uppercase" data-id="element-54">
              ¿Te suena familiar esta historia?
            </h2>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4" data-id="element-55">
            {[{
            emoji: '🎰',
            text: 'Mirar resultados de apuestas deportivas a las 3 AM.'
          }, {
            emoji: '📉',
            text: "Pedir préstamos 'gota a gota' o microcréditos para recuperar lo perdido."
          }, {
            emoji: '🤫',
            text: 'Esconder el celular de tu pareja o familia por vergüenza.'
          }, {
            emoji: '💔',
            text: "Sentir que el 'Casi Gano' es la única salida a tus deudas."
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-56">
                <div className="bg-carbon p-4 md:p-5 rounded-xl border-l-4 border-gold flex items-start gap-4 hover:bg-darker-surface transition-colors" data-id="element-57">
                  <span className="text-3xl md:text-4xl flex-shrink-0" data-id="element-58">
                    {item.emoji}
                  </span>
                  <p className="text-base md:text-lg font-bold text-white leading-snug" data-id="element-59">
                    {item.text}
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* SECTION 3: QUÉ RECIBES HOY */}
      <section className="py-8 md:py-12 px-4 max-w-7xl mx-auto" data-id="element-60">
        {/* 4 Reviews */}
        <FadeIn data-id="element-61">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-id="element-62">
            {reviews.map((review, i) => <div key={i} className="bg-darker-surface rounded-xl border border-gold/20 p-4 flex flex-col gap-2" data-id="element-63">
                <div className="flex gap-0.5 mb-1" data-id="element-64">
                  {Array.from({
                length: review.stars
              }).map((_, s) => <span key={s} className="text-gold text-sm" data-id="element-65">
                      ★
                    </span>)}
                </div>
                <p className="text-body-gray text-sm italic leading-relaxed flex-grow" data-id="element-66">
                  "{review.text}"
                </p>
                <div className="mt-2 pt-2 border-t border-gold/10" data-id="element-67">
                  <p className="text-gold font-bold text-sm" data-id="element-68">{review.name}</p>
                  <p className="text-white/40 text-xs" data-id="element-69">{review.location}</p>
                </div>
              </div>)}
          </div>
        </FadeIn>

        <FadeIn data-id="element-70">
          <h2 className="text-3xl md:text-5xl text-gold text-center mb-6 text-glow-gold uppercase" data-id="element-71">
            Lo que recibes hoy en tu email
          </h2>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-72">
          {[{
          badge: 'TOMO 1',
          title: 'LA RADIOGRAFÍA DEL ABISMO',
          desc: 'Identifica cómo las máquinas y apps engañan a tu cerebro. Para hombres y mujeres de 20 a 65 años.'
        }, {
          badge: 'TOMO 2',
          title: 'EL CEREBRO SECUESTRADO',
          desc: 'El manual para vencer la ansiedad química y romper el ciclo de la mentira.'
        }, {
          badge: 'TOMO 3',
          title: 'EL MANUAL DE DESCONEXIÓN',
          desc: 'Blindaje financiero real y tu plan de libertad de 21 días.'
        }].map((tomo, i) => <FadeIn key={i} delay={i * 0.2} data-id="element-73">
              <div className="bg-darker-surface rounded-2xl border-t-4 border-gold p-4 md:p-5 h-full flex flex-col items-center text-center shadow-2xl" data-id="element-74">
                <div className="w-full overflow-hidden rounded-lg mb-3 relative bg-carbon" data-id="element-75">
                  <p className="text-gold font-black text-xs md:text-sm uppercase tracking-wider text-center py-2 px-3 bg-gold/10 border-b border-gold/20" data-id="element-76">
                    EL SISTEMA DEFINITIVO PARA ROMPER CON LA LUDOPATÍA
                  </p>
                  <div className="aspect-[4/5]" data-id="element-77">
                    <img src={HERO_IMAGE} alt={tomo.title} className="w-full h-full object-cover object-top opacity-90" data-id="element-78" />
                    <div className="absolute inset-0 bg-gradient-to-t from-darker-surface via-transparent to-transparent" data-id="element-79"></div>
                  </div>
                </div>
                <span className="bg-gold/20 text-gold font-bold px-4 py-1 rounded-full text-sm mb-2 tracking-wider" data-id="element-80">
                  {tomo.badge}
                </span>
                <h3 className="text-xl md:text-2xl text-gold font-black mb-2 uppercase" data-id="element-81">
                  {tomo.title}
                </h3>
                <p className="text-body-gray flex-grow" data-id="element-82">{tomo.desc}</p>
              </div>
            </FadeIn>)}
        </div>

        <FadeIn delay={0.6} data-id="element-83">
          <BookCarousel data-id="element-84" />
        </FadeIn>
      </section>

      {/* SECTION: BONOS */}
      <section className="py-8 md:py-12 bg-dark-surface px-4" data-id="element-85">
        <div className="max-w-4xl mx-auto" data-id="element-86">
          <FadeIn data-id="element-87">
            <h2 className="text-3xl md:text-5xl text-gold text-center mb-3 text-glow-gold uppercase" data-id="element-88">
              🎁 Bonos exclusivos
            </h2>
            <p className="text-body-gray text-center mb-6 text-lg" data-id="element-89">
              Además de los 3 tomos, hoy te llevas estos bonos totalmente gratis
            </p>
          </FadeIn>

          <div className="flex flex-col gap-3" data-id="element-90">
            {[{
            emoji: '🚨',
            title: 'BONO 1: El Botón de Pánico',
            value: '$15.900',
            desc: 'Una guía de emergencia paso a paso para cuando sientas el impulso incontrolable de apostar. Técnicas de 60 segundos que cortan la ansiedad en seco.'
          }, {
            emoji: '🛡️',
            title: 'BONO 2: El Escudo Financiero',
            value: '$12.500',
            desc: 'Plantillas y estrategias para blindar tu dinero: cómo bloquear accesos a apps de apuestas, configurar límites bancarios y crear un plan de recuperación de deudas.'
          }, {
            emoji: '⚡',
            title: 'BONO 3: Desintoxicación de Dopamina',
            value: '$19.900',
            desc: 'Un protocolo de 7 días para resetear tu sistema de recompensa cerebral. Reemplaza el subidón de la apuesta por hábitos que realmente te dan bienestar.'
          }].map((bono, i) => <FadeIn key={i} delay={i * 0.15} data-id="element-91">
                <div className="bg-carbon rounded-2xl border border-gold/20 p-4 md:p-6 flex items-start gap-4 shadow-lg" data-id="element-92">
                  <span className="text-3xl md:text-4xl flex-shrink-0 mt-1" data-id="element-93">
                    {bono.emoji}
                  </span>
                  <div data-id="element-94">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5" data-id="element-95">
                      <h3 className="text-lg md:text-2xl text-gold font-black uppercase" data-id="element-96">
                        {bono.title}
                      </h3>
                      <span className="bg-casino-red/20 text-casino-red font-bold px-3 py-1 rounded-full text-xs tracking-wider" data-id="element-97">
                        Valor: {bono.value} — GRATIS
                      </span>
                    </div>
                    <p className="text-body-gray leading-snug" data-id="element-98">{bono.desc}</p>
                  </div>
                </div>
              </FadeIn>)}
          </div>

          <FadeIn delay={0.5} data-id="element-99">
            <div className="mt-5 text-center" data-id="element-100">
              <p className="text-gold font-bold text-lg md:text-xl" data-id="element-101">
                Valor total de los bonos:{' '}
                <span className="line-through text-body-gray" data-id="element-102">$48.300</span>{' '}
                <span className="text-casino-red font-black" data-id="element-103">→ GRATIS HOY</span>
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 4: TESTIMONIO / PROMESA */}
      <section className="py-8 bg-dark-surface px-4" data-id="element-104">
        <div className="max-w-3xl mx-auto" data-id="element-105">
          <FadeIn data-id="element-106">
            <div className="border-2 border-gold rounded-2xl p-5 md:p-7 relative bg-carbon text-center shadow-[0_0_30px_rgba(214,160,30,0.1)]" data-id="element-107">
              <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-4xl text-gold bg-dark-surface px-4 leading-none" data-id="element-108">
                "
              </div>
              <p className="text-lg md:text-xl text-body-gray italic leading-relaxed" data-id="element-109">
                No necesitas fuerza de voluntad sobrehumana. Necesitas un{' '}
                <span className="text-gold font-bold not-italic" data-id="element-110">
                  SISTEMA DE FRICCIÓN
                </span>
                . Al terminar estos 3 tomos, el juego dejará de ser una opción
                en tu mente.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 5: PRECIO Y OFERTA */}
      <section id="oferta" className="py-8 md:py-12 px-4 max-w-4xl mx-auto text-center" data-id="element-111">
        <FadeIn data-id="element-112">
          <h2 className="text-3xl md:text-5xl text-gold mb-4 text-glow-gold uppercase" data-id="element-113">
            Obtén el sistema completo hoy
          </h2>

          <div className="mb-5" data-id="element-114">
            <p className="text-xl md:text-2xl text-body-gray line-through mb-1" data-id="element-115">
              Antes $47.999
            </p>
            <p className="text-5xl md:text-7xl text-gold font-black tracking-tighter text-glow-gold" data-id="element-116">
              HOY SOLO $19.999
            </p>
            <p className="text-base text-body-gray mt-2" data-id="element-117">
              Acceso inmediato a los 3 tomos en Formato Digital PDF
            </p>
          </div>

          <a href="https://pagos.romahome.shop/cart/48863169872114:1" target="_blank" rel="noopener noreferrer" className="w-full md:w-auto bg-casino-red text-white font-black uppercase py-4 px-8 md:px-14 rounded-xl text-lg md:text-2xl shadow-glow-red hover:bg-red-600 transition-all animate-cta-pulse mb-4 inline-block text-center" data-id="element-118">
            DESCARGAR TODO EL SISTEMA POR $19.999
          </a>

          <div className="flex sm:flex-row gap-4 sm:gap-8 text-body-gray text-sm md:text-base flex-row items-start justify-center" data-id="element-119">
            <div className="flex items-center gap-2" data-id="element-120">
              <Lock className="text-gold" size={20} data-id="element-121" />
              <span data-id="element-122">Pago 100% Seguro</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-123">
              <CheckCircle className="text-gold" size={20} data-id="element-124" />
              <span data-id="element-125">Acceso Inmediato</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-126">
              <Mail className="text-gold" size={20} data-id="element-127" />
              <span data-id="element-128">Envío a tu Correo</span>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* SECTION 6: FAQ */}
      <section className="py-8 bg-dark-surface px-4" data-id="element-129">
        <div className="max-w-3xl mx-auto" data-id="element-130">
          <FadeIn data-id="element-131">
            <h2 className="text-3xl md:text-4xl text-gold text-center mb-5 text-glow-gold uppercase" data-id="element-132">
              Preguntas Frecuentes
            </h2>

            <div className="bg-carbon rounded-2xl p-4 md:p-6 border border-gold/20" data-id="element-133">
              <FAQItem question="¿Sirve si mi adicción es a las apps de fútbol?" answer="Sí, cubrimos el algoritmo de las apuestas deportivas en detalle. El sistema funciona para cualquier tipo de apuesta, incluyendo fútbol, casino online y tragamonedas." data-id="element-134" />
              <FAQItem question="¿Cómo recibo el material?" answer="Es un producto 100% digital (PDF). Lo recibes directamente en tu email en segundos después de completar el pago." data-id="element-135" />
              <FAQItem question="¿Qué pasa si no me funciona?" answer="Confiamos en el sistema. Si en 30 días no ves resultados, te devolvemos tu dinero sin preguntas." data-id="element-136" />
              <FAQItem question="¿La compra es anónima? ¿Mi familia se va a enterar?" answer="Sí, completamente anónima. El cobro aparece en tu tarjeta con un nombre genérico y el material llega a tu email personal. Nadie de tu entorno se entera de nada." data-id="element-137" />
              <FAQItem question="¿En cuánto tiempo voy a ver resultados?" answer="La mayoría de nuestros lectores reportan una reducción significativa del impulso en las primeras 2 semanas. El plan completo de 21 días del Tomo 3 está diseñado para consolidar tu libertad de forma permanente." data-id="element-138" />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-4 border-t border-white/5 text-center px-4" data-id="element-139">
        <p className="text-body-gray text-sm mb-1" data-id="element-140">
          © 2026 El Casi Gano. Todos los derechos reservados.
        </p>
        <p className="text-white/30 text-xs max-w-2xl mx-auto" data-id="element-141">
          Este es un producto digital de autoayuda. Los resultados pueden variar
          dependiendo del compromiso individual.
        </p>
      </footer>

      {/* Purchase notification popup */}
      <PurchasePopup data-id="element-142" />
    </div>;
}